import {
  hashKey,
  type QueryCacheNotifyEvent,
  type QueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import type { ItemTreeSet, ItemTreeSetEntry } from '@/common/api/types/items';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { createExpiringIdRegistry } from '@/common/utils/expiringIdRegistry';
import { lazyMapFor } from '@/common/utils/lazyMapFor';
import { readsReportingStoreTreeSet } from '@/features/items/queryKeys/itemsTreeSetKey';
import { SELF_EDIT_ATTRIBUTION_TTL_MS } from './itemsTreeSetConstants';
import {
  clearHeldArrangement,
  getHeldArrangement,
  stageHeldArrangement,
} from './itemsTreeSetHeldArrangement';
import { clearMovedNotice, publishMovedNotice } from './itemsTreeSetMovedNotice';
import {
  clearRemovedNotice,
  isExcludedFromRemovedNotice,
  publishRemovedNotice,
} from './itemsTreeSetRemovedNotice';
import {
  allChangedIds,
  diffTreeSet,
  isEmptyTreeSetDiff,
  type TreeSetDiff,
  visibleChangedIds,
  withAncestors,
  withDescendants,
  withNewAncestors,
} from './treeSetDiff';

export type TreeSetPredicate = (entries: ItemTreeSetEntry[]) => boolean;

// Under `reportingServiceFilter` the tree-set is an eventually-consistent mirror, so one refetch
// can still return the pre-write tree.
const FAST_POLL_MAX_ATTEMPTS = 8;
const FAST_POLL_RETRY_DELAY_MS = 500;
const BACKGROUND_SYNC_INTERVAL_MS = 30_000;
// Lets one tier's recent fetch stand in for the other's, so they don't double-fetch seconds apart.
const RECENT_FETCH_WINDOW_MS = 5_000;

// Unmounted variants are skipped: a stale item-store copy would always look caught up.
const renderedTreeSetsMatching = (
  queryClient: QueryClient,
  predicate: (query: { queryKey: readonly unknown[] }) => boolean,
) =>
  queryClient
    .getQueriesData<ItemTreeSet>({ queryKey: commonQueryKeys.itemsTreeSet.all(), type: 'active' })
    .filter(([queryKey]) => predicate({ queryKey }))
    .flatMap(([, treeSet]) => (treeSet ? [treeSet] : []));

const renderedTreeSets = (queryClient: QueryClient) =>
  renderedTreeSetsMatching(queryClient, () => true);

export const treeSetHasItems =
  (itemIds: number[]): TreeSetPredicate =>
  (entries) => {
    const ids = new Set(entries.map((entry) => entry.id));
    return itemIds.every((id) => ids.has(id));
  };

export const treeSetLacksItems =
  (itemIds: number[]): TreeSetPredicate =>
  (entries) => {
    const ids = new Set(entries.map((entry) => entry.id));
    return itemIds.every((id) => !ids.has(id));
  };

/** Satisfied once these rows stop reporting their pre-write parent — the new one, or someone else's. */
export const treeSetReparented =
  (previousParentById: ReadonlyMap<number, number | null>, newParentId: number | null) =>
  (entries: ItemTreeSetEntry[]) => {
    const parentById = new Map(entries.map((entry) => [entry.id, entry.parentId]));
    const noParent = (parentId: number | null | undefined) => parentId ?? null;
    return [...previousParentById].every(([id, previousParentId]) => {
      if (!parentById.has(id)) return true;
      const parentId = noParent(parentById.get(id));
      return parentId === noParent(newParentId) || parentId !== noParent(previousParentId);
    });
  };

/** Satisfied once `movedId` sits on `anchorId`'s requested side in flat order — not adjacency, since descendants can sit between them. */
export const treeSetReordered =
  (movedId: number, anchorId: number, direction: 'before' | 'after'): TreeSetPredicate =>
  (entries) => {
    // One pass for both ids, not two findIndex scans — this tree-set can run into the thousands.
    let movedIdx = -1;
    let anchorIdx = -1;
    for (let i = 0; i < entries.length && (movedIdx === -1 || anchorIdx === -1); i++) {
      const id = entries[i]?.id;
      if (id === movedId) movedIdx = i;
      else if (id === anchorId) anchorIdx = i;
    }
    // Absent from this variant entirely — irrelevant, not evidence either way.
    if (movedIdx === -1) return true;
    // Present without its anchor (e.g. filtered out) — not evidence the move landed.
    if (anchorIdx === -1) return false;
    // A reorder never crosses parents — matching order under different parents is coincidence.
    if ((entries[movedIdx]?.parentId ?? null) !== (entries[anchorIdx]?.parentId ?? null)) {
      return false;
    }
    return direction === 'after' ? movedIdx > anchorIdx : movedIdx < anchorIdx;
  };

/** Parents these rows are rendered under; ids nothing renders are omitted. */
export const readTreeSetParents = (
  queryClient: QueryClient,
  itemIds: number[],
): Map<number, number | null> => {
  const wanted = new Set(itemIds);
  const parentById = new Map<number, number | null>();
  for (const treeSet of renderedTreeSets(queryClient)) {
    for (const entry of treeSet.data) {
      if (wanted.has(entry.id) && !parentById.has(entry.id)) {
        parentById.set(entry.id, entry.parentId);
      }
    }
  }
  return parentById;
};

/** Item-store is eagerly refetched; reporting-store is only marked stale, so it doesn't race whatever reconciliation the caller runs next. */
export const invalidateItemsTreeSet = (queryClient: QueryClient): Promise<void> =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: commonQueryKeys.itemsTreeSet.all(),
      predicate: (query) => !readsReportingStoreTreeSet(query),
    }),
    queryClient.invalidateQueries({
      queryKey: commonQueryKeys.itemsTreeSet.all(),
      predicate: readsReportingStoreTreeSet,
      refetchType: 'none',
    }),
  ]).then(() => undefined);

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Whether this tree-set's current snapshot contains any of the rows a poll is watching. */
const observesAnyOf = (entries: readonly ItemTreeSetEntry[], ids: readonly number[]) => {
  if (ids.length === 0) return true;
  const present = new Set(entries.map((entry) => entry.id));
  return ids.some((id) => present.has(id));
};

const lastFetchAtByClient = new WeakMap<QueryClient, number>();
const markFetched = (queryClient: QueryClient) => lastFetchAtByClient.set(queryClient, Date.now());
const fetchedRecently = (queryClient: QueryClient) => {
  const at = lastFetchAtByClient.get(queryClient);
  return at !== undefined && Date.now() - at < RECENT_FETCH_WINDOW_MS;
};

const hasMountedReportingStoreVariant = (queryClient: QueryClient) =>
  renderedTreeSetsMatching(queryClient, readsReportingStoreTreeSet).length > 0;

// --- Attribution: this session's own recently-edited ids ---

// Lets a later `reconcileAndSettle` call reuse attribution an earlier one recorded, instead of
// restarting the scope. Tier 2 never consults this — it holds unconditionally either way.
//
// Known gap: per-client, not per-edit — can't tell "my own side effect" from "another user's
// concurrent edit" to the same descendant. Needs server-side edit provenance to fix properly.
const selfEditedIdsRegistry = createExpiringIdRegistry(SELF_EDIT_ATTRIBUTION_TTL_MS);

/** Called by every mutation that reconciles the tree-set, so a later diff can attribute the change. */
export const recordSelfEditedIds = (queryClient: QueryClient, itemIds: readonly number[]): void =>
  selfEditedIdsRegistry.record(queryClient, itemIds);

const selfEditedIds = (queryClient: QueryClient): ReadonlySet<number> =>
  selfEditedIdsRegistry.ids(queryClient);

/** Prefers an id this session actually edited over an arbitrary one from the diff. */
export const preferSelfEditedId = (
  queryClient: QueryClient,
  ids: readonly number[],
): number | undefined => {
  const edited = selfEditedIds(queryClient);
  return ids.find((id) => edited.has(id)) ?? ids[0];
};

const editingGuardCountByClient = new WeakMap<QueryClient, number>();

const isEditingGuarded = (queryClient: QueryClient) =>
  (editingGuardCountByClient.get(queryClient) ?? 0) > 0;

// --- Classify each reconciled tree-set and either promote it live or hold it for the user ---

// Keyed by client then query key — concurrent attempts on the same key (tier 1 + tier 2, or two
// predicate polls) join one fetch instead of each issuing their own.
type InFlightFetch = { promise: Promise<ItemTreeSet | undefined>; startedAt: number };
const inFlightFetchesByClient = new WeakMap<QueryClient, Map<string, InFlightFetch>>();

/**
 * Invokes a query's own `queryFn` directly, bypassing `fetchQuery`/`refetchQueries` — both write
 * into the cache and notify observers immediately, which is exactly the promotion classify needs
 * to gate. `startedAt` is the joined fetch's own start time, not the caller's — see `FetchedTreeSet`.
 */
const fetchWithoutPromoting = (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): InFlightFetch => {
  const queryKeyString = hashKey(queryKey);
  let inFlight = inFlightFetchesByClient.get(queryClient);
  if (!inFlight) {
    inFlight = new Map();
    inFlightFetchesByClient.set(queryClient, inFlight);
  }
  const existing = inFlight.get(queryKeyString);
  if (existing) return existing;

  const startedAt = performance.now();
  const query = queryClient.getQueryCache().find<ItemTreeSet>({ queryKey });
  if (!query) return { promise: Promise.resolve(undefined), startedAt };
  const queryFn = query.options.queryFn;
  if (typeof queryFn !== 'function') return { promise: Promise.resolve(undefined), startedAt };
  const context: QueryFunctionContext<readonly unknown[]> = {
    queryKey,
    client: queryClient,
    // Required by the type; no tree-set `queryFn` reads it, so this fetch isn't actually cancellable.
    signal: new AbortController().signal,
    meta: query.options.meta,
  };
  const attempt = inFlight;
  const promise = Promise.resolve(queryFn(context)).finally(() => {
    attempt.delete(queryKeyString);
  });
  const entry = { promise, startedAt };
  inFlight.set(queryKeyString, entry);
  return entry;
};

type ReconciliationTrigger = 'tier1' | 'tier2';

/** One mounted variant's fresh fetch, handed to `evaluateReflected`. */
export type FetchedTreeSet = {
  queryKey: readonly unknown[];
  data: ItemTreeSet;
  startedAt: number;
};

/** A predicate-mode poll round, threaded through `refetchRenderedTreeSets` and `promoteOrStageReportingStoreTreeSets`. */
type PredicateRound = {
  /** Decides whether this round's fetched variants count as convergence — see `defaultEvaluateReflected`. */
  evaluateReflected: (fetched: readonly FetchedTreeSet[]) => boolean;
  /**
   * Rechecked once each fetch resolves — the caller only checks it before and after the whole
   * fetch/classify call, which is too late to stop an individual query's write once its round has
   * been superseded mid-fetch.
   */
  isCancelled: () => boolean;
};

/** Default evidence rule: prefer variants observing `changedItemIds`, else fall back to all
 *  fetched variants (delete's absence-as-evidence case). Reorder overrides this — see `useItemPriorityReorder.ts`. */
const defaultEvaluateReflected =
  (isReflected: TreeSetPredicate, changedItemIds: readonly number[]) =>
  (fetched: readonly FetchedTreeSet[]): boolean => {
    const contributions = fetched.map(({ data }) => ({
      observes: observesAnyOf(data.data, changedItemIds),
      reflects: isReflected(data.data),
    }));
    const observing = contributions.filter((c) => c.observes);
    const evaluated = observing.length > 0 ? observing : contributions;
    return evaluated.some((c) => c.reflects);
  };

// A shared fetch must be classified exactly once — otherwise a second caller diffs against a
// `current` snapshot the first's promotion already made stale. Keyed by promise identity, so it
// needs no explicit cleanup.
const classifiedFetches = new WeakSet<Promise<ItemTreeSet | undefined>>();

// `matched`/`itemTypeId` catch a field edit that flips filter membership without moving the row —
// id/parentId alone would miss it.
const treeSetFingerprint = (entries: readonly ItemTreeSetEntry[]): string =>
  entries
    .map((entry) => `${entry.id}:${entry.parentId ?? ''}:${entry.matched}:${entry.itemTypeId}`)
    .join('|');

// `stageHeldArrangement` always resets `dismissed` to false — skip the write when the offer is
// unchanged since it was staged, so a dismissed banner doesn't pop back open every tick.
const stageIfChanged = (queryKeyString: string, fresh: ItemTreeSet, diff: TreeSetDiff): void => {
  const existing = getHeldArrangement(queryKeyString);
  if (existing && treeSetFingerprint(existing.treeSet.data) === treeSetFingerprint(fresh.data)) {
    return;
  }
  stageHeldArrangement(queryKeyString, fresh, diff);
};

/**
 * Fetches each active reporting-store tree-set without promoting it, so the diff below always
 * compares against the still-rendered structure. A diff confined to this session's own edited rows
 * (and their descendants) promotes as before; anything wider is staged instead — offered, not imposed.
 *
 * `trigger` narrows further for tier 2: by the time it runs, tier 1 already gave up and enough time
 * has passed that an edit isn't safely "fresh" — so tier 2 always holds, regardless of attribution.
 *
 * `respectEditingGuard` discards a result if an editor opened mid-fetch (other entry checks only
 * cover one already open before the attempt starts). `false` for predicate-mode polls, which aren't
 * paused by an editor on another row.
 *
 * `predicateRound`, when given, only computes the return value and gates evidence/cancellation for
 * it — it never changes what's written by the classify/promote/stage path below.
 *
 * One query failing must not sink the others in this `Promise.all`, nor throw as unhandled — just
 * no update this round.
 */
const promoteOrStageReportingStoreTreeSets = async (
  queryClient: QueryClient,
  trigger: ReconciliationTrigger,
  respectEditingGuard: boolean,
  predicateRound?: PredicateRound,
): Promise<boolean> => {
  const entries = queryClient
    .getQueriesData<ItemTreeSet>({ queryKey: commonQueryKeys.itemsTreeSet.all(), type: 'active' })
    .filter(([queryKey]) => readsReportingStoreTreeSet({ queryKey }));
  const editedIds = selfEditedIds(queryClient);
  const fetchedEntries: FetchedTreeSet[] = [];
  // A failed fetch is absent from `fetchedEntries`, not merely stale — unlike an unmounted
  // variant, it might have observed the change, so it must not read as vacuously satisfied.
  let hadFailedFetch = false;

  await Promise.all(
    entries.map(async ([queryKey, current]) => {
      const queryKeyString = hashKey(queryKey);
      try {
        const { promise: fetchPromise, startedAt } = fetchWithoutPromoting(queryClient, queryKey);
        const fresh = await fetchPromise;
        if (!fresh) return;
        if (respectEditingGuard && isEditingGuarded(queryClient)) return;
        // The caller only checks cancellation before/after this whole call — too late for a
        // response that resolves after its own round was superseded mid-fetch.
        if (predicateRound?.isCancelled()) return;

        if (predicateRound) {
          fetchedEntries.push({ queryKey, data: fresh, startedAt });
        }

        if (classifiedFetches.has(fetchPromise)) return;
        classifiedFetches.add(fetchPromise);

        // No baseline yet for this key — nothing to hold onto.
        if (!current) {
          queryClient.setQueryData<ItemTreeSet>(queryKey, fresh);
          clearHeldArrangement(queryKeyString);
          return;
        }

        const diff = diffTreeSet(current.data, fresh.data);
        if (isEmptyTreeSetDiff(diff)) {
          queryClient.setQueryData<ItemTreeSet>(queryKey, fresh);
          clearHeldArrangement(queryKeyString);
          return;
        }

        if (trigger !== 'tier1') {
          stageIfChanged(queryKeyString, fresh, diff);
          return;
        }

        const scope = withNewAncestors(
          withAncestors(withDescendants(editedIds, current.data), current.data),
          diff.addedIds,
          fresh.data,
        );
        const attributable = scope.size > 0 && allChangedIds(diff).every((id) => scope.has(id));

        if (attributable) {
          queryClient.setQueryData<ItemTreeSet>(queryKey, fresh);
          // A stale hold no longer applies once this key has moved on — accepting it would revert
          // this promotion.
          clearHeldArrangement(queryKeyString);
          if (diff.movedIds.length > 0) publishMovedNotice(queryKeyString, diff);
          // Skips ids the user explicitly deleted — nothing left to open a panel onto.
          const removedIds = diff.removedIds.filter(
            (id) => !isExcludedFromRemovedNotice(queryClient, id),
          );
          if (removedIds.length > 0) publishRemovedNotice(queryKeyString, { ...diff, removedIds });
        } else {
          stageIfChanged(queryKeyString, fresh, diff);
        }
      } catch {
        // Best effort — the next tick tries again.
        hadFailedFetch = true;
      }
    }),
  );

  if (!predicateRound) return true;
  // A failed fetch means this round's evidence is incomplete — never mind what it does say.
  if (hadFailedFetch) return false;
  // Nothing fetched this round isn't evidence of convergence.
  if (fetchedEntries.length === 0) return false;
  return predicateRound.evaluateReflected(fetchedEntries);
};

/**
 * Promotes an accepted arrangement, then withdraws the offer. Returns the ids now visibly
 * different (excludes `removedIds` — no row left to highlight) so a caller can mark them settled.
 */
export const applyHeldTreeSetArrangement = (
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): readonly number[] => {
  const queryKeyString = hashKey(queryKey);
  const held = getHeldArrangement(queryKeyString);
  if (!held) return [];
  queryClient.setQueryData<ItemTreeSet>(queryKey, held.treeSet);
  clearHeldArrangement(queryKeyString);
  return visibleChangedIds(held.diff);
};

const refetchRenderedTreeSets = async (
  queryClient: QueryClient,
  trigger: ReconciliationTrigger,
  respectEditingGuard: boolean,
  predicateRound?: PredicateRound,
): Promise<boolean> => {
  // item-store is read-your-writes — never the evidence a poll evaluates, never needs holding.
  const hasReportingStoreVariant = hasMountedReportingStoreVariant(queryClient);
  let reflectedByFreshFetch = true;
  if (hasReportingStoreVariant) {
    reflectedByFreshFetch = await promoteOrStageReportingStoreTreeSets(
      queryClient,
      trigger,
      respectEditingGuard,
      predicateRound,
    );
  } else {
    await queryClient.refetchQueries(
      { queryKey: commonQueryKeys.itemsTreeSet.all(), type: 'active' },
      { cancelRefetch: false },
    );
  }
  markFetched(queryClient);
  return reflectedByFreshFetch;
};

// --- Tier 1's diff-based stop condition, for a write no predicate can express ---

const snapshotReportingStoreTreeSets = (queryClient: QueryClient): Map<string, string> => {
  const snapshot = new Map<string, string>();
  for (const [queryKey, treeSet] of queryClient.getQueriesData<ItemTreeSet>({
    queryKey: commonQueryKeys.itemsTreeSet.all(),
    type: 'active',
  })) {
    if (!treeSet || !readsReportingStoreTreeSet({ queryKey })) continue;
    snapshot.set(hashKey(queryKey), treeSetFingerprint(treeSet.data));
  }
  return snapshot;
};

/** True once a tree-set present at both times has a different shape than it did at `baseline`. */
const hasAnyTreeSetChangedSince = (
  queryClient: QueryClient,
  baseline: ReadonlyMap<string, string>,
): boolean => {
  for (const [key, fingerprint] of snapshotReportingStoreTreeSets(queryClient)) {
    const before = baseline.get(key);
    if (before !== undefined && before !== fingerprint) return true;
  }
  return false;
};

// --- Tier 1: fast, bounded polling armed by this session's own mutation ---

type DiffReconciliationToken = { cancelled: boolean; reassertLocalWrite?: () => void };
const activeDiffReconciliationByClient = new WeakMap<QueryClient, DiffReconciliationToken>();

/**
 * Cancels the in-flight diff-based (no-predicate) reconciliation for this client, if any.
 * Predicate-based reconciliations (create, reparent, delete, reorder) poll independently and are
 * unaffected.
 */
export const cancelItemsTreeSetReconciliation = (queryClient: QueryClient) => {
  const token = activeDiffReconciliationByClient.get(queryClient);
  if (token) token.cancelled = true;
  activeDiffReconciliationByClient.delete(queryClient);
};

// --- Predicate-mode reconciliation: opt-in per-key supersession ---

type PredicateReconciliationToken = { cancelled: boolean };
const activePredicateReconciliationsByClient = new WeakMap<
  QueryClient,
  Map<string, PredicateReconciliationToken>
>();

/**
 * Cancels the in-flight predicate-mode reconciliation registered under `key`, if any — opt-in and
 * keyed by the caller, unlike diff mode's single client-wide slot.
 */
export const cancelPredicateReconciliation = (queryClient: QueryClient, key: string): void => {
  const tokens = activePredicateReconciliationsByClient.get(queryClient);
  const token = tokens?.get(key);
  if (token) token.cancelled = true;
  tokens?.delete(key);
};

/**
 * Repeatedly refetches the tree-set until it reflects a write, so the grid doesn't settle on the
 * mirror's pre-write structure. Resolves `false` if it never converges within `maxAttempts`.
 *
 * With `isReflected` (create, reparent, delete, reorder), that predicate is the stop condition and
 * each call polls independently. Without it, the stop condition is "has anything changed since this
 * call started" — the only signal available for a field edit. These calls share one slot per
 * client: a new one cancels whichever is still running (inheriting its `reassertLocalWrite`, so
 * an unrelated edit can't drop it), so a burst of edits converges once. `setItemsTreeSetEditingGuard`
 * also cancels, without inheriting, and blocks starting this slot while an editor is open.
 *
 * Callers still invalidate the tree-set separately — this only covers the refetch-until-correct part.
 */
export const reconcileItemsTreeSet = async (
  queryClient: QueryClient,
  {
    isReflected,
    evaluateReflected,
    changedItemIds = [],
    maxAttempts = FAST_POLL_MAX_ATTEMPTS,
    retryDelayMs = FAST_POLL_RETRY_DELAY_MS,
    reassertLocalWrite,
    supersedeKey,
  }: {
    isReflected?: TreeSetPredicate;
    /** Overrides how a round decides convergence, given every fetched variant. Defaults to `defaultEvaluateReflected`; only drag reorder needs its own — see `useItemPriorityReorder.ts`. */
    evaluateReflected?: (fetched: readonly FetchedTreeSet[]) => boolean;
    changedItemIds?: readonly number[];
    maxAttempts?: number;
    retryDelayMs?: number;
    /**
     * Called after each non-converging attempt, once the fetched data has already been checked
     * against `baseline` — never before, or a reasserted local write could misread as
     * convergence. Restores the caller's own cache write that the attempt's refetch just
     * overwrote, including after the final attempt, so giving up still shows the caller's write
     * instead of the last stale response.
     */
    reassertLocalWrite?: () => void;
    /** Predicate-mode only — registers this call under `key` for `cancelPredicateReconciliation`. Diff mode ignores it. */
    supersedeKey?: string;
  } = {},
): Promise<boolean> => {
  // Stays off while an editor is open, so it can't reorder the row underneath it.
  if (!isReflected && isEditingGuarded(queryClient)) return false;
  // No predicate means no early-exit signal, so skip an item-store-only mount entirely — it's
  // already correct and would otherwise burn the whole attempt budget refetching it for nothing.
  if (!isReflected && !hasMountedReportingStoreVariant(queryClient)) return true;

  let token: DiffReconciliationToken | undefined;
  let combinedReassertLocalWrite = reassertLocalWrite;
  if (!isReflected) {
    // Hands off the outgoing call's reassertion rather than dropping it — its own loop never runs
    // again once cancelled, so nothing else would reapply e.g. a drag-reorder's row order.
    const outgoingReassert = activeDiffReconciliationByClient.get(queryClient)?.reassertLocalWrite;
    if (outgoingReassert) {
      combinedReassertLocalWrite = reassertLocalWrite
        ? () => {
            outgoingReassert();
            reassertLocalWrite();
          }
        : outgoingReassert;
    }
    cancelItemsTreeSetReconciliation(queryClient);
    token = { cancelled: false, reassertLocalWrite: combinedReassertLocalWrite };
    activeDiffReconciliationByClient.set(queryClient, token);
    // Yields once so a synchronous burst of calls cancels every rival but the last before any
    // of them reaches the network.
    await Promise.resolve();
    if (token.cancelled) return false;
  }

  let predicateToken: PredicateReconciliationToken | undefined;
  if (isReflected && supersedeKey !== undefined) {
    // Defensive — the caller is expected to have already cancelled this key.
    cancelPredicateReconciliation(queryClient, supersedeKey);
    predicateToken = { cancelled: false };
    lazyMapFor(activePredicateReconciliationsByClient, queryClient).set(
      supersedeKey,
      predicateToken,
    );
  }
  const isCancelled = () => Boolean(token?.cancelled) || Boolean(predicateToken?.cancelled);

  const predicateRound: PredicateRound | undefined = isReflected
    ? {
        evaluateReflected:
          evaluateReflected ?? defaultEvaluateReflected(isReflected, changedItemIds),
        isCancelled,
      }
    : undefined;

  const baseline = isReflected ? undefined : snapshotReportingStoreTreeSets(queryClient);

  try {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (isCancelled()) return false;
      const reflectedByFreshFetch = await refetchRenderedTreeSets(
        queryClient,
        'tier1',
        !isReflected,
        predicateRound,
      );
      if (isCancelled()) return false;
      const converged = isReflected
        ? reflectedByFreshFetch
        : hasAnyTreeSetChangedSince(queryClient, baseline as ReadonlyMap<string, string>);
      if (converged) return true;
      combinedReassertLocalWrite?.();
      if (attempt < maxAttempts) await delay(retryDelayMs);
    }
    return false;
  } finally {
    if (token !== undefined && activeDiffReconciliationByClient.get(queryClient) === token) {
      activeDiffReconciliationByClient.delete(queryClient);
    }
    if (
      supersedeKey !== undefined &&
      activePredicateReconciliationsByClient.get(queryClient)?.get(supersedeKey) === predicateToken
    ) {
      activePredicateReconciliationsByClient.get(queryClient)?.delete(supersedeKey);
    }
  }
};

// --- Tier 2: slow, unarmed background sync ---

const isDocumentVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

const runBackgroundSyncTick = async (queryClient: QueryClient) => {
  if (!isDocumentVisible() || isEditingGuarded(queryClient) || fetchedRecently(queryClient)) return;
  // Item-store is read-your-writes already, so a tick there would just repaint for nothing.
  if (!hasMountedReportingStoreVariant(queryClient)) return;
  await refetchRenderedTreeSets(queryClient, 'tier2', true);
};

// Rides react-query's own gcTime instead of a separate TTL — all three stores share this key space.
const startQueryRemovalCleanup = (queryClient: QueryClient): (() => void) =>
  queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
    if (event.type !== 'removed') return;
    if (!readsReportingStoreTreeSet({ queryKey: event.query.queryKey })) return;
    const queryKeyString = hashKey(event.query.queryKey);
    clearHeldArrangement(queryKeyString);
    clearMovedNotice(queryKeyString);
    clearRemovedNotice(queryKeyString);
  });

type BackgroundSync = {
  intervalId: ReturnType<typeof setInterval>;
  stopQueryRemovalCleanup: () => void;
};
const backgroundSyncByClient = new WeakMap<QueryClient, BackgroundSync>();
const syncingClients = new Set<QueryClient>();
let visibilityListener: (() => void) | undefined;

const attachVisibilityListenerOnce = () => {
  if (visibilityListener || typeof document === 'undefined') return;
  visibilityListener = () => {
    if (!isDocumentVisible()) return;
    // Catches up immediately rather than waiting out however much of the interval is left.
    for (const queryClient of syncingClients) void runBackgroundSyncTick(queryClient);
  };
  document.addEventListener('visibilitychange', visibilityListener);
};

// Detaches once nothing is syncing, so no global listener state outlives the last stopped client.
const detachVisibilityListenerIfIdle = () => {
  if (syncingClients.size > 0 || !visibilityListener || typeof document === 'undefined') return;
  document.removeEventListener('visibilitychange', visibilityListener);
  visibilityListener = undefined;
};

/**
 * Starts the slow, unarmed background sync — refetches a mounted reporting-store tree-set every
 * `intervalMs`, whether or not this session made a change. No-ops while the tab is hidden, an
 * editor is open, or tier 1 just ran; catches up immediately on tab focus. Idempotent — only one
 * interval runs per client.
 */
export const startItemsTreeSetBackgroundSync = (
  queryClient: QueryClient,
  { intervalMs = BACKGROUND_SYNC_INTERVAL_MS }: { intervalMs?: number } = {},
) => {
  if (backgroundSyncByClient.has(queryClient)) return;
  attachVisibilityListenerOnce();
  syncingClients.add(queryClient);
  const intervalId = setInterval(() => void runBackgroundSyncTick(queryClient), intervalMs);
  const stopQueryRemovalCleanup = startQueryRemovalCleanup(queryClient);
  backgroundSyncByClient.set(queryClient, { intervalId, stopQueryRemovalCleanup });
};

export const stopItemsTreeSetBackgroundSync = (queryClient: QueryClient) => {
  const sync = backgroundSyncByClient.get(queryClient);
  if (!sync) return;
  clearInterval(sync.intervalId);
  sync.stopQueryRemovalCleanup();
  backgroundSyncByClient.delete(queryClient);
  syncingClients.delete(queryClient);
  detachVisibilityListenerIfIdle();
};

/**
 * Marks whether a cell/row editor is open — pauses background sync and cancels/blocks diff-mode
 * polling while `true`; predicate-based polling (create/reparent/delete/reorder) is unaffected.
 * Ref-counted, so concurrent editors don't unpause each other.
 *
 * Closing the last editor re-checks whatever `promoteOrStageReportingStoreTreeSets` discarded while
 * open — otherwise the hold would sit until tier 2's next tick, up to `BACKGROUND_SYNC_INTERVAL_MS`
 * later.
 */
export const setItemsTreeSetEditingGuard = (queryClient: QueryClient, isEditing: boolean): void => {
  const current = editingGuardCountByClient.get(queryClient) ?? 0;
  const next = isEditing ? current + 1 : Math.max(0, current - 1);
  editingGuardCountByClient.set(queryClient, next);
  if (isEditing) {
    cancelItemsTreeSetReconciliation(queryClient);
  } else if (current > 0 && next === 0) {
    // Fire-and-forget from a UI event — best effort; a rejection here must not go unhandled.
    refetchRenderedTreeSets(queryClient, 'tier1', false).catch(() => {});
  }
};
